import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { useToast } from '@contexts/Toast';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import { errorMessage, toastMessage } from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { License, NewOrderInitiationProps, State } from '@helpers/Models';
import { CustomerOrderService } from '@services/CustomerService';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import TextField from '@mui/material/TextField';
import { formatDate } from '@services/OrderCreationService';
import { useUnsavedChangesPrompt } from '@hooks/useNavigationBlocker';
import { useFetchStateName } from '@hooks/useFetchStateName';

export const LicenseDetails: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { getStateName } = useFetchStateName();
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const [customerId, setCustomerId] = useState<number>(0);
  const [states, setStates] = useState<State[]>([]);
  const { showConfirmationToast } = UseConfirmationToast();
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();
  // License state
  const [licenses, setLicenses] = useState<License[]>([
    {
      id: -1,
      harvestStateId: 0,
      firstName: customerDetails?.firstName || '',
      lastName: customerDetails?.lastName || '',
      licenseNumber: '',
      expiryDate: '',
    },
  ]);
  const [editingLicenseIndex, setEditingLicenseIndex] = useState<number | null>(
    null,
  );
  const [showContinueOrderButton, setShowContinueOrderButton] =
    useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  useUnsavedChangesPrompt(hasUnsavedChanges);

  /**
   * Effect hook that initializes the customer context and fetches available butcher species.
   *
   * @remarks
   * This effect runs once on component mount. It attempts to retrieve customer details from
   * local storage. If customer details are found, it sets the customer ID in state.
   * If not, it redirects the user to the customer list page.
   * Regardless of whether customer details are found, it triggers a fetch of butcher species data.
   */
  useEffect(() => {
    const customerID = CustomerOrderService.getCustomerId();
    const customerDetails = CustomerOrderService.getCustomerDetails();

    setCustomerDetails(customerDetails);

    if (customerID) {
      setCustomerId(customerID);
      fetchStates();
      fetchLicenseDetails(customerID, customerDetails);
    } else {
      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
  }, []);

  /**
   * Fetches the list of butcher species from the server and initializes their quantities.
   *
   * @remarks
   * This asynchronous function sends a POST request to fetch master data of type `1` from the backend.
   * Once the species data is received, it initializes the `quantity` property of each species to `0`
   * and updates the component state via `setSpecies`.
   *
   * If an error occurs during the fetch, an error toast is displayed to the user.
   */
  const fetchStates = async () => {
    try {
      const response = await Axios.get(ApiEndpoints.FETCH_STATES);
      setStates(response.data.data);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches license details for a given customer and updates the license state accordingly.
   *
   * @async
   * @function fetchLicenseDetails
   * @param {number} id - The unique identifier of the customer whose license details are to be fetched.
   * @param {Object} details - Additional customer details (used as fallback if no license data is found).
   * @param {string} [details.firstName] - Customer's first name (used if no license record exists).
   * @param {string} [details.lastName] - Customer's last name (used if no license record exists).
   *
   * @returns {Promise<void>} Resolves when the license details are fetched and state is updated.
   *
   * @throws {Error} Displays an error toast if the request fails.
   *
   * @description
   * - Makes an API call to fetch customer license details.
   * - If no license exists, initializes a placeholder license record with default values
   *   and sets the editing index to `0`.
   * - If licenses exist, updates the licenses state and enables the "Continue Order" button.
   * - Handles API errors gracefully by showing a toast error message.
   *
   */
  const fetchLicenseDetails = async (
    id: number,
    details?: NewOrderInitiationProps,
  ): Promise<void> => {
    try {
      const response = await Axios.post(
        ApiEndpoints.FETCH_CUSTOMER_LICENSE_DETAILS,
        { customerId: id },
      );

      if (
        !response.data.data.licenses ||
        response.data.data.licenses.length === 0
      ) {
        setLicenses([
          {
            id: -1,
            harvestStateId: 0,
            firstName: details?.firstName || '',
            lastName: details?.lastName || '',
            licenseNumber: '',
            expiryDate: '',
          },
        ]);
        setEditingLicenseIndex(0);
      } else {
        setLicenses(response.data.data.licenses);
        // Update the newly added license details in the local storage
        const existingCustomerDetails =
          CustomerOrderService.getCustomerDetails();

        // Merge new licenses into existing customer object
        const updatedCustomerDetails = {
          ...existingCustomerDetails,
          HuntingLicences: response.data.data.licenses,
        };
        // Save back to localStorage
        localStorage.setItem(
          'customerDetails',
          JSON.stringify(updatedCustomerDetails),
        );

        setEditingLicenseIndex(null);
        // Display continue order button only when license details exists
        setShowContinueOrderButton(true);
      }
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Updates a specific field of a license at the given index in the licenses state.
   *
   * @function handleLicenseChange
   * @param {number} index - The index of the license in the licenses array to update.
   * @param {keyof License} field - The license field (property name) to update.
   * @param {string | number} value - The new value to assign to the specified field.
   *
   * @returns {void} Does not return anything; updates the licenses state.
   *
   * @description
   * - Creates a shallow copy of the licenses state.
   * - Updates the given field of the license at the specified index.
   * - Returns the new licenses array, ensuring immutability of state.
   *
   */
  const handleLicenseChange = (
    index: number,
    field: keyof License,
    value: string | number,
  ): void => {
    setHasUnsavedChanges(true);
    setLicenses((prev) => {
      const updatedLicenses = [...prev];
      updatedLicenses[index] = {
        ...updatedLicenses[index],
        [field]: value,
      };
      return updatedLicenses;
    });
  };

  /**
   * Handles the cancellation of license editing by showing a confirmation toast.
   *
   * @function cancelLicense
   * @returns {void} Does not return anything; triggers a confirmation toast.
   *
   * @description
   * - Displays a confirmation toast with a discard message.
   * - Provides an action button labeled "Discard".
   * - Calls `onClose` when the discard action is confirmed.
   *
   */
  const cancelLicense = (): void => {
    showConfirmationToast(
      toastMessage.discardConfirmation,
      'Discard Changes',
      'delete',
      onClose,
      'Discard', // Action button name
    );
  };

  /**
   * Handles saving or updating a customer's license details.
   *
   * @async
   * @function handleSaveLicenseDetails
   * @param {React.FormEvent} e - The form submission event to prevent default behavior.
   * @param {number} index - The index of the license in the licenses array to save.
   *
   * @returns {Promise<void>} Resolves once the license is validated, saved, and state is updated.
   *
   * @description
   * - Prevents default form submission behavior.
   * - Retrieves the license item at the given index from state.
   * - Validates that all required license fields are filled in.
   *   - If validation fails, shows an error toast and stops execution.
   * - Builds a `payload` object for the API request, formatting dates and applying fallbacks from `customerDetails`.
   * - Calls the `CUSTOMER_LICENSE_UPSERT` API endpoint to create or update the license.
   * - Refreshes license details from the server after saving.
   * - Shows a success toast if the operation completes successfully.
   * - Updates the license in local state with the response data (if provided).
   * - Handles errors gracefully by showing an error toast.
   *
   */
  const handleSaveLicenseDetails = async (
    e: React.FormEvent,
    index: number,
  ) => {
    e.preventDefault();
    setHasUnsavedChanges(false);
    try {
      const licenseItem = licenses[index];
      if (
        !licenseItem.firstName?.trim() ||
        !licenseItem.lastName?.trim() ||
        !licenseItem.harvestStateId ||
        !licenseItem.licenseNumber?.trim() ||
        !licenseItem.expiryDate?.trim()
      ) {
        showToastMessage(errorMessage.LICENSE_VALIDATION_FAILURE, 'error');
        return;
      }

      const payload = {
        id: licenseItem?.id ?? -1,
        harvestStateId: licenseItem?.harvestStateId ?? 0,
        firstName: licenseItem?.firstName || customerDetails?.firstName || '',
        lastName: licenseItem?.lastName || customerDetails?.lastName || '',
        licenseNumber: licenseItem?.licenseNumber || '',
        expiryDate: licenseItem?.expiryDate
          ? dayjs(licenseItem.expiryDate).format('MM/DD/YYYY')
          : '',
      };

      const response = await Axios.post(ApiEndpoints.CUSTOMER_LICENSE_UPSERT, {
        customerId: customerId,
        license: payload,
      });
      fetchLicenseDetails(customerId, customerDetails);
      setEditingLicenseIndex(null);

      if (response.data?.data?.license) {
        const updated = [...licenses];
        updated[index] = response.data.data.license;
        setLicenses(updated);
      }
    } catch (error) {
      showToastMessage(errorMessage.ERROR_UPDATING_CUSTOMER, 'error');
    }
  };

  /**
   * Adds a new license entry to the licenses state.
   *
   * @function handleAddLicense
   * @returns {void} Does not return anything; updates licenses state.
   *
   * @description
   * - Prevents adding a new license if one is already being edited (`editingLicenseIndex !== null`).
   *   - In this case, shows an error toast instead.
   * - Creates a new license object with default values and customer name prefilled.
   * - Appends the new license to the licenses array in state.
   * - Sets the editing index to the newly added license, enabling inline editing.
   *
   */
  const handleAddLicense = () => {
    // If already editing a license, don’t allow adding another
    if (editingLicenseIndex !== null) {
      showToastMessage(errorMessage.ERROR_ADDING_CUSTOMER_LICENSE, 'error');
      return;
    }

    const newLicense = {
      id: -1,
      harvestStateId: 0,
      firstName: customerDetails?.firstName || '',
      lastName: customerDetails?.lastName || '',
      licenseNumber: '',
      expiryDate: '',
    };

    setLicenses((prev) => [...prev, newLicense]);
    setEditingLicenseIndex(licenses.length);
  };

  /**
   * Sets the editing mode for a specific license by updating the editingLicenseIndex state.
   *
   * @param index - The index of the license to be edited.
   */
  const editLicense = (index: number) => {
    setEditingLicenseIndex(index);
  };

  /**
   * Handles closing the modal.
   *
   * If there are no unsaved changes, it will immediately close the modal
   * by calling `onClose`. If unsaved changes exist, it will instead call
   * `cancelLicense` to show a warning or perform cleanup.
   *
   * @function handleClose
   *
   * @returns {void}
   *
   */
  const handleClose = () => {
    // If no unsaved changes, dismiss the modal otherwise, show the warning toast
    if (!hasUnsavedChanges) {
      onClose();
    } else {
      cancelLicense();
    }
  };

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white w-full max-w-2xl max-h-[95vh] overflow-y-auto rounded-lg shadow-lg p-3">
          {/* Modal Header */}
          <div className="flex justify-between items-center p-1">
            <h2 className="text-xl font-medium">License</h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-600 hover:text-gray-900"
            >
              ✕
            </button>
          </div>
          {/* Add new license form */}
          <div className="p-1">
            {licenses.map((license, index) => (
              <div
                key={license.id}
                className="bg-[#FFFEFA] mb-3 mt-3 shadown-lg rounded-lg mx-3 p-3 border border-gray-300"
              >
                {editingLicenseIndex === index ? (
                  <>
                    <div className="flex w-full justify-start items-end">
                      <div className="w-1/3">
                        <label htmlFor="firstName" className="block mb-4">
                          First Name <sup>*</sup>
                        </label>
                        <input
                          name="firstName"
                          type="text"
                          value={license.firstName}
                          onChange={(e) =>
                            handleLicenseChange(
                              index,
                              'firstName',
                              e.target.value,
                            )
                          }
                          placeholder="First Name"
                          className="p-2 border rounded-md text-gray-600 w-11/12 lg:w-9/12 focus:outline-primary"
                        />
                      </div>

                      <div className="w-1/3">
                        <label htmlFor="lastName" className="block mb-4">
                          Last Name <sup>*</sup>
                        </label>
                        <input
                          name="lastName"
                          type="text"
                          value={license.lastName}
                          onChange={(e) =>
                            handleLicenseChange(
                              index,
                              'lastName',
                              e.target.value,
                            )
                          }
                          placeholder="Last Name"
                          className="p-2 border rounded-md text-gray-600 w-11/12 lg:w-9/12 focus:outline-primary"
                        />
                      </div>

                      <div className="w-1/3">
                        <label htmlFor="state" className="block mb-4">
                          State <sup>*</sup>
                        </label>
                        <select
                          name="state"
                          value={license.harvestStateId}
                          onChange={(e) =>
                            handleLicenseChange(
                              index,
                              'harvestStateId',
                              parseInt(e.target.value),
                            )
                          }
                          className="p-2 border rounded-md text-gray-600 w-11/12 lg:w-9/12 focus:outline-primary"
                        >
                          <option value="">Select state</option>
                          {states.map((state) => (
                            <option key={state.id} value={state.id}>
                              {state.stateName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="w-full flex justify-start items-end mb-4">
                      <div className="w-1/3">
                        <label htmlFor="licenseNumber" className="block mb-4">
                          License Number <sup>*</sup>
                        </label>
                        <input
                          name="licenseNumber"
                          type="text"
                          value={license.licenseNumber}
                          onChange={(e) =>
                            handleLicenseChange(
                              index,
                              'licenseNumber',
                              e.target.value,
                            )
                          }
                          placeholder="License Number"
                          className="p-2 border rounded-md text-gray-600 w-11/12 lg:w-9/12 focus:outline-primary"
                        />
                      </div>
                      <div className="w-1/3 relative">
                        <label htmlFor="expiryDate" className="block mb-4">
                          License Expiry <sup>*</sup>
                        </label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disablePast
                            format="MM/DD/YYYY"
                            value={dayjs(license.expiryDate)}
                            onChange={(newValue) => {
                              if (newValue) {
                                const formattedDate =
                                  newValue.format('MM/DD/YYYY');
                                handleLicenseChange(
                                  index,
                                  'expiryDate',
                                  formattedDate,
                                );
                              }
                            }}
                            desktopModeMediaQuery="@media (min-width: 0px)"
                            slots={{ textField: TextField }}
                            slotProps={{
                              textField: {
                                placeholder: 'MM/DD/YYYY',
                                size: 'small',
                                sx: {
                                  textDecorationColor: '#4b5563',
                                  backgroundColor: 'white',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                },
                                InputProps: {
                                  sx: {
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    '&.Mui-focused': {
                                      borderColor: '#240089',
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </div>
                    </div>

                    <div className="flex align-middle justify-end items-center">
                      <div>
                        <button
                          onClick={cancelLicense}
                          className="text-primary font-bold"
                          type="button"
                        >
                          Cancel
                        </button>
                      </div>
                      <div>
                        <button
                          onClick={(event) =>
                            handleSaveLicenseDetails(event, index)
                          }
                          className="bg-primary w-24 text-white px-4 ml-8 py-2 my-8 rounded-md"
                          type="button"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  // Displaying License Details – added text wrapping
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-lg">License Details</p>
                      {license.isEditable == 1 && (
                        <button
                          onClick={() => editLicense(index)}
                          className="flex items-center gap-1 text-primary hover:text-primary-dark"
                          type="button"
                        >
                          <FontAwesomeIcon icon={faPen} />
                          <span className="font-bold">Edit</span>
                        </button>
                      )}
                    </div>

                    {/* First row: First Name, Last Name, State */}
                    <div className="flex w-full justify-start items-start">
                      <div className="w-1/3">
                        <label className="block mb-2 text-sm text-black opacity-25">
                          First Name
                        </label>
                        <p className="break-words pr-2">{license.firstName}</p>
                      </div>

                      <div className="w-1/3">
                        <label className="block mb-2 text-sm text-black opacity-25">
                          Last Name
                        </label>
                        <p className="break-words pr-2">{license.lastName}</p>
                      </div>

                      <div className="w-1/3">
                        <label className="block mb-2 text-sm text-black opacity-25">
                          State
                        </label>
                        <p className="break-words pr-2">
                          {getStateName(license.harvestStateId)}
                        </p>
                      </div>
                    </div>

                    {/* Second row: License Number, License Expiry */}
                    <div className="flex w-full justify-start items-start mb-4">
                      <div className="w-1/3">
                        <label className="block mb-2 text-sm text-black opacity-25">
                          License Number
                        </label>
                        <p className="break-words pr-2">
                          {license.licenseNumber}
                        </p>
                      </div>
                      <div className="w-1/3 relative">
                        <label className="block mb-2 text-sm text-black opacity-25">
                          License Expiry
                        </label>
                        <p className="break-words pr-2">
                          {license.expiryDate && formatDate(license.expiryDate)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* License footer buttons */}
          <div id="add-license" className="mx-3">
            <button
              type="button"
              onClick={handleAddLicense}
              className="w-full px-4 py-2 rounded-md bg-white text-primary border border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              + Add License
            </button>
            {/* Display Continue Order button only when license details exists. 
                    Otherwise restrict the species customization screen navigation. */}
            {showContinueOrderButton && (
              <button
                type="button"
                onClick={onClose}
                className="w-full text-primary px-4 py-2 my-3 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all"
              >
                Continue Order
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};